import { useState, useRef, useEffect, useCallback } from 'react';
import { Check, RotateCcw, Move } from 'lucide-react';

interface Point {
  x: number;
  y: number;
}

interface DocumentCropperProps {
  imageData: string;
  onCrop: (croppedBlob: Blob) => void;
  onCancel: () => void;
}

export default function DocumentCropper({ imageData, onCrop, onCancel }: DocumentCropperProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const outputCanvasRef = useRef<HTMLCanvasElement>(null);
  
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });
  
  // Corners as percentages (0-100) for responsive positioning
  const [corners, setCorners] = useState<Point[]>([
    { x: 10, y: 10 },   // Top-left
    { x: 90, y: 10 },   // Top-right
    { x: 90, y: 90 },   // Bottom-right
    { x: 10, y: 90 },   // Bottom-left
  ]);
  
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

  // Handle image load
  const handleImageLoad = useCallback(() => {
    const img = imageRef.current;
    const container = containerRef.current;
    if (!img || !container) return;

    setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
    setDisplaySize({ width: img.clientWidth, height: img.clientHeight });
    setImageLoaded(true);
  }, []);

  // Update display size on resize
  useEffect(() => {
    const handleResize = () => {
      const img = imageRef.current;
      if (img) {
        setDisplaySize({ width: img.clientWidth, height: img.clientHeight });
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Convert percentage to pixels
  const toPixels = (corner: Point) => ({
    x: (corner.x / 100) * displaySize.width,
    y: (corner.y / 100) * displaySize.height,
  });

  // Convert pixels to percentage
  const toPercent = (x: number, y: number) => ({
    x: Math.max(0, Math.min(100, (x / displaySize.width) * 100)),
    y: Math.max(0, Math.min(100, (y / displaySize.height) * 100)),
  });

  // Handle drag start
  const handleDragStart = (index: number) => (e: React.TouchEvent | React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDraggingIndex(index);
  };

  // Handle drag move
  const handleDragMove = useCallback((e: TouchEvent | MouseEvent) => {
    if (draggingIndex === null) return;
    e.preventDefault();

    const img = imageRef.current;
    if (!img) return;

    const rect = img.getBoundingClientRect();
    let clientX: number, clientY: number;

    if ('touches' in e && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else if ('clientX' in e) {
      clientX = e.clientX;
      clientY = e.clientY;
    } else {
      return;
    }

    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const newPos = toPercent(x, y);

    setCorners(prev => {
      const updated = [...prev];
      updated[draggingIndex] = newPos;
      return updated;
    });
  }, [draggingIndex, displaySize]);

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    setDraggingIndex(null);
  }, []);

  // Add/remove global event listeners for dragging
  useEffect(() => {
    if (draggingIndex !== null) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove, { passive: false });
      window.addEventListener('touchend', handleDragEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', handleDragMove);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [draggingIndex, handleDragMove, handleDragEnd]);

  const resetCorners = () => {
    setCorners([
      { x: 10, y: 10 },
      { x: 90, y: 10 },
      { x: 90, y: 90 },
      { x: 10, y: 90 },
    ]);
  };

  const applyCrop = useCallback(async () => {
    const img = imageRef.current;
    const outputCanvas = outputCanvasRef.current;
    if (!img || !outputCanvas) return;

    const ctx = outputCanvas.getContext('2d');
    if (!ctx) return;

    // Convert percentage corners to actual image coordinates
    const imgCorners = corners.map(c => ({
      x: (c.x / 100) * imageSize.width,
      y: (c.y / 100) * imageSize.height,
    }));

    // Calculate output dimensions
    const width = Math.max(
      Math.sqrt(Math.pow(imgCorners[1].x - imgCorners[0].x, 2) + Math.pow(imgCorners[1].y - imgCorners[0].y, 2)),
      Math.sqrt(Math.pow(imgCorners[2].x - imgCorners[3].x, 2) + Math.pow(imgCorners[2].y - imgCorners[3].y, 2))
    );
    const height = Math.max(
      Math.sqrt(Math.pow(imgCorners[3].x - imgCorners[0].x, 2) + Math.pow(imgCorners[3].y - imgCorners[0].y, 2)),
      Math.sqrt(Math.pow(imgCorners[2].x - imgCorners[1].x, 2) + Math.pow(imgCorners[2].y - imgCorners[1].y, 2))
    );

    outputCanvas.width = width;
    outputCanvas.height = height;

    // Create source image
    const sourceImg = new Image();
    sourceImg.crossOrigin = 'anonymous';
    
    await new Promise<void>((resolve) => {
      sourceImg.onload = () => resolve();
      sourceImg.src = imageData;
    });

    // Perspective transform via bilinear interpolation
    const steps = 80;
    for (let y = 0; y < steps; y++) {
      for (let x = 0; x < steps; x++) {
        const u = x / steps;
        const v = y / steps;
        const u1 = (x + 1) / steps;
        const v1 = (y + 1) / steps;

        const srcX = (1 - u) * (1 - v) * imgCorners[0].x +
                     u * (1 - v) * imgCorners[1].x +
                     u * v * imgCorners[2].x +
                     (1 - u) * v * imgCorners[3].x;
        const srcY = (1 - u) * (1 - v) * imgCorners[0].y +
                     u * (1 - v) * imgCorners[1].y +
                     u * v * imgCorners[2].y +
                     (1 - u) * v * imgCorners[3].y;

        const srcX1 = (1 - u1) * (1 - v1) * imgCorners[0].x +
                      u1 * (1 - v1) * imgCorners[1].x +
                      u1 * v1 * imgCorners[2].x +
                      (1 - u1) * v1 * imgCorners[3].x;
        const srcY1 = (1 - u1) * (1 - v1) * imgCorners[0].y +
                      u1 * (1 - v1) * imgCorners[1].y +
                      u1 * v1 * imgCorners[2].y +
                      (1 - u1) * v1 * imgCorners[3].y;

        ctx.drawImage(
          sourceImg,
          srcX, srcY, Math.abs(srcX1 - srcX) + 1, Math.abs(srcY1 - srcY) + 1,
          u * width, v * height, width / steps + 1, height / steps + 1
        );
      }
    }

    outputCanvas.toBlob((blob) => {
      if (blob && blob.size > 0) {
        onCrop(blob);
      } else {
        // Fallback: if cropping fails, use original image
        console.warn('Crop produced empty blob, using original image');
        fetch(imageData)
          .then(res => res.blob())
          .then(originalBlob => onCrop(originalBlob))
          .catch(err => console.error('Failed to get original image:', err));
      }
    }, 'image/jpeg', 0.92);
  }, [corners, imageSize, imageData, onCrop]);

  // Generate SVG path for the selection overlay
  const getOverlayPath = () => {
    if (!imageLoaded) return '';
    const c = corners.map(toPixels);
    return `M0,0 L${displaySize.width},0 L${displaySize.width},${displaySize.height} L0,${displaySize.height} Z 
            M${c[0].x},${c[0].y} L${c[1].x},${c[1].y} L${c[2].x},${c[2].y} L${c[3].x},${c[3].y} Z`;
  };

  const getSelectionPath = () => {
    if (!imageLoaded) return '';
    const c = corners.map(toPixels);
    return `M${c[0].x},${c[0].y} L${c[1].x},${c[1].y} L${c[2].x},${c[2].y} L${c[3].x},${c[3].y} Z`;
  };

  return (
    <div ref={containerRef} className="flex flex-col h-full bg-black rounded-2xl overflow-hidden">
      <div className="p-4 bg-slate-900">
        <h3 className="text-white font-medium flex items-center space-x-2">
          <Move className="w-5 h-5 text-shuttle-sand" />
          <span>Drag the circles to crop</span>
        </h3>
        <p className="text-white/60 text-sm mt-1">Move all 4 corners to outline your document</p>
      </div>

      <div className="flex-1 overflow-auto flex items-center justify-center p-2 bg-slate-950">
        <div className="relative inline-block" style={{ touchAction: 'none' }}>
          {/* The image */}
          <img
            ref={imageRef}
            src={imageData}
            alt="Document to crop"
            onLoad={handleImageLoad}
            className="max-w-full max-h-[50vh] block"
            style={{ touchAction: 'none' }}
            draggable={false}
          />

          {imageLoaded && (
            <>
              {/* Dark overlay with cutout */}
              <svg
                className="absolute inset-0 pointer-events-none"
                width={displaySize.width}
                height={displaySize.height}
              >
                <path
                  d={getOverlayPath()}
                  fill="rgba(0,0,0,0.6)"
                  fillRule="evenodd"
                />
                <path
                  d={getSelectionPath()}
                  fill="none"
                  stroke="#f59e0b"
                  strokeWidth="3"
                />
              </svg>

              {/* Draggable corner handles - BIG touch targets */}
              {corners.map((corner, index) => {
                const pos = toPixels(corner);
                const labels = ['1', '2', '3', '4'];
                return (
                  <div
                    key={index}
                    onMouseDown={handleDragStart(index)}
                    onTouchStart={handleDragStart(index)}
                    className={`absolute cursor-grab active:cursor-grabbing select-none ${
                      draggingIndex === index ? 'z-20' : 'z-10'
                    }`}
                    style={{
                      left: pos.x,
                      top: pos.y,
                      transform: 'translate(-50%, -50%)',
                      touchAction: 'none',
                    }}
                  >
                    {/* Large invisible touch target */}
                    <div 
                      className="absolute w-16 h-16 -left-8 -top-8"
                      style={{ touchAction: 'none' }}
                    />
                    {/* Visible handle */}
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold shadow-lg border-4 transition-transform ${
                        draggingIndex === index
                          ? 'bg-amber-500 border-amber-300 scale-125'
                          : 'bg-white border-amber-500'
                      }`}
                      style={{ touchAction: 'none' }}
                    >
                      <span className={draggingIndex === index ? 'text-white' : 'text-slate-800'}>
                        {labels[index]}
                      </span>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>

      <canvas ref={outputCanvasRef} className="hidden" />

      <div className="p-4 bg-slate-900 flex flex-wrap justify-center gap-3">
        <button
          onClick={onCancel}
          className="px-5 py-3 bg-slate-700 text-white rounded-xl hover:bg-slate-600 transition-colors text-sm"
        >
          Cancel
        </button>
        <button
          onClick={resetCorners}
          className="px-5 py-3 bg-slate-700 text-white rounded-xl hover:bg-slate-600 transition-colors flex items-center space-x-2 text-sm"
        >
          <RotateCcw className="w-4 h-4" />
          <span>Reset</span>
        </button>
        <button
          onClick={applyCrop}
          className="px-5 py-3 bg-gradient-to-r from-shuttle-sand to-amber-500 text-shuttle-dark rounded-xl font-medium hover:from-amber-400 hover:to-amber-500 transition-colors flex items-center space-x-2 text-sm"
        >
          <Check className="w-4 h-4" />
          <span>Apply Crop</span>
        </button>
      </div>
    </div>
  );
}
